import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Sparkles } from "lucide-react";
import QedaraLogo from "@/components/QedaraLogo";

export default function AuthScreen() {
  const { login, register, forgot } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "register" | "forgot"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && password !== confirm) {
      return toast.error("Parolele nu se potrivesc");
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Bine ai revenit");
      } else if (mode === "register") {
        await register(name, email, password);
        toast.success("Cont creat");
      } else {
        await forgot(email);
        toast.success("Dacă adresa există, am trimis un link de resetare.");
        setMode("login");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Autentificarea nu a reușit");
    } finally {
      setLoading(false);
    }
  };

  const heading = {
    login: { tag: "// AUTENTIFICARE", title: "Bine ai revenit." },
    register: { tag: "// CONT NOU", title: "Intri în echipă." },
    forgot: { tag: "// RESETARE PAROLĂ", title: "Ai uitat parola?" },
  }[mode];

  const cta = mode === "login" ? "Autentifică-te" : mode === "register" ? "Creează cont" : "Trimite linkul de resetare";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-bg text-fg">
      {/* Left: Form */}
      <div className="flex flex-col p-8 lg:p-16 justify-between min-h-screen">
        <QedaraLogo variant="full" markSize={40} />

        <div className="max-w-md w-full mx-auto lg:mx-0">
          <p className="label-tiny mb-4">{heading.tag}</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter mb-3">{heading.title}</h1>
          <p className="text-fg/60 mb-10 text-sm leading-relaxed">
            {mode === "forgot"
              ? "Scrie-ți adresa de email și îți trimitem un link ca să-ți pui o parolă nouă."
              : "Un spațiu în care echipa ta discută, livrează proiecte și scrie cod cu AI."}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="label-tiny block mb-2">Nume</label>
                <input
                  data-testid="auth-name-input"
                  className="input-flat"
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Ada Lovelace" required
                />
              </div>
            )}
            <div>
              <label className="label-tiny block mb-2">Email</label>
              <input
                data-testid="auth-email-input"
                type="email" required
                className="input-flat"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@firma.ro"
              />
            </div>
            {mode !== "forgot" && (
              <div>
                <label className="label-tiny block mb-2">Parolă</label>
                <input
                  data-testid="auth-password-input"
                  type="password" required minLength={6}
                  className="input-flat"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            )}
            {mode === "register" && (
              <div>
                <label className="label-tiny block mb-2">Confirmă parola</label>
                <input
                  data-testid="auth-confirm-input"
                  type="password" required minLength={6}
                  className="input-flat"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            )}

            {mode === "login" && (
              <div className="text-right">
                <button
                  type="button"
                  data-testid="auth-forgot-link"
                  onClick={() => setMode("forgot")}
                  className="text-xs text-fg/50 hover:text-brand-2"
                >
                  Ai uitat parola?
                </button>
              </div>
            )}

            <button
              data-testid="auth-submit-button"
              type="submit" disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 mt-6"
            >
              {loading ? "..." : cta}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-6 text-sm text-fg/60">
            {mode === "forgot" ? (
              <button
                data-testid="auth-back-to-login"
                onClick={() => setMode("login")}
                className="text-brand hover:text-brand-2 font-medium"
              >
                ← Înapoi la autentificare
              </button>
            ) : (
              <>
                {mode === "login" ? "Prima oară aici?" : "Ai deja cont?"}{" "}
                <button
                  data-testid="auth-toggle-mode"
                  onClick={() => setMode(mode === "login" ? "register" : "login")}
                  className="text-brand hover:text-brand-2 font-medium"
                >
                  {mode === "login" ? "Creează cont" : "Autentifică-te"}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="label-tiny">
          v0.1 · pentru echipe care livrează
        </div>
      </div>

      {/* Right: branded glow panel (no raster image) */}
      <div className="relative hidden lg:flex items-center justify-center overflow-hidden bg-bg">
        <div
          className="absolute -top-24 -left-24 w-[28rem] h-[28rem] rounded-full blur-3xl opacity-30"
          style={{ background: "radial-gradient(circle, rgb(var(--accent-2)), transparent 70%)" }}
        />
        <div
          className="absolute top-1/3 right-0 w-[26rem] h-[26rem] rounded-full blur-3xl opacity-25"
          style={{ background: "radial-gradient(circle, rgb(var(--accent)), transparent 70%)" }}
        />
        <div
          className="absolute -bottom-24 left-1/4 w-[30rem] h-[30rem] rounded-full blur-3xl opacity-25"
          style={{ background: "radial-gradient(circle, rgb(var(--accent) / 0.7), transparent 70%)" }}
        />

        <div className="relative z-10 flex flex-col items-center text-center px-16">
          <QedaraLogo variant="full" markSize={96} />
          <div className="flex items-center gap-2 mt-12 mb-3">
            <Sparkles className="w-4 h-4 text-brand-2" />
            <p className="label-tiny">// PROGRAMATOR AI</p>
          </div>
          <h2 className="text-3xl font-bold tracking-tighter max-w-lg leading-tight">
            Discutați, planificați și scrieți cod împreună — într-un singur spațiu de lucru.
          </h2>
          <p className="text-fg/50 mt-4 max-w-md text-sm">
            Conectează orice model compatibil OpenAI sau modelele tale Ollama.
          </p>
        </div>
      </div>
    </div>
  );
}
